import { IMAGES } from "@/lib/constants";
import ScrollReveal from "@/components/ScrollReveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Quote } from "lucide-react";

const impactData = [
  { metric: "Farmers in FPO Network", current: 5000, currentLabel: "5,000+", target: "100,000", percent: 5 },
  { metric: "Women-Led Households Served", current: 1800, currentLabel: "1,800+", target: "10,000+", percent: 18 },
  { metric: "Villages Reached", current: 100, currentLabel: "100+", target: "500+", percent: 20 },
  { metric: "Youth Volunteers Trained", current: 200, currentLabel: "200+", target: "5,000+", percent: 4 },
  { metric: "Health Camps Conducted", current: 30, currentLabel: "30+", target: "200+", percent: 15 },
  { metric: "Community Hospitals Planned", current: 0, currentLabel: "In Progress", target: "2-3", percent: 0 },
  { metric: "Districts with Active Presence", current: 1, currentLabel: "Khunti", target: "4 Districts", percent: 25 },
];

const galleryImages = [
  { src: IMAGES.agriculture, alt: "Farmers with agricultural tools" },
  { src: IMAGES.womenEmpowerment, alt: "Women distributing supplies" },
  { src: IMAGES.seeds, alt: "Seed distribution" },
  { src: IMAGES.distribution, alt: "Community distribution drive" },
  { src: IMAGES.children, alt: "Children nutrition programme" },
  { src: IMAGES.youth, alt: "Youth empowerment event" },
  { src: IMAGES.events, alt: "Community event" },
  { src: IMAGES.arjunMunda, alt: "Community leadership" },
  { src: IMAGES.heroHome, alt: "Village gathering" },
];

const testimonials = [
  {
    quote: "Before MBT, I had no way to sell my crops at a fair price. Now, through the FPO, I earn twice what I used to — and my daughter is in school for the first time.",
    name: "Farmer",
    location: "Khunti District",
    image: IMAGES.seeds,
  },
  {
    quote: "The self-help group gave me the confidence and skills to start my own small business. Today, I support my family and help other women in my village do the same.",
    name: "SHG Leader",
    location: "Lohardaga District",
    image: IMAGES.womenEmpowerment,
  },
  {
    quote: "I joined as a youth volunteer and found my calling. MBT showed me that young people can lead real change — not someday, but right now.",
    name: "Youth Volunteer",
    location: "Ranchi District",
    image: IMAGES.youth,
  },
];

export default function ImpactPage() {
  return (
    <main data-testid="impact-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.events})` }} />
        <div className="absolute inset-0 bg-[#1B4332]/65" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-20">
          <ScrollReveal>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-4">
              Measured Impact.<br />Not Just Good Intentions.
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Impact Dashboard */}
      <section data-testid="impact-dashboard" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">By The Numbers</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Impact Dashboard</h2>
            </div>
          </ScrollReveal>
          <div className="space-y-6">
            {impactData.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div className="bg-[#F0F7F4] rounded-xl p-5 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                    <h3 className="font-outfit font-semibold text-[#1B4332] text-base">{item.metric}</h3>
                    <div className="flex items-center gap-4 text-sm font-outfit">
                      <span className="text-[#D4A843] font-bold">
                        {item.current > 0 ? <AnimatedCounter target={item.current} suffix="+" /> : item.currentLabel}
                      </span>
                      <span className="text-[#666]">Target: {item.target}</span>
                    </div>
                  </div>
                  <div className="w-full bg-[#1B4332]/10 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-[#2D6A4F] h-full rounded-full transition-all duration-1000"
                      style={{ width: `${Math.max(item.percent, 2)}%` }}
                    />
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section data-testid="photo-gallery" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">From The Field</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Impact Gallery</h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((img, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div className="overflow-hidden rounded-xl group">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-40 sm:h-52 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-carousel" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Voices from the Ground</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">What People Say</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <ScrollReveal key={i} delay={i * 0.15}>
                <div className="bg-[#F0F7F4] rounded-xl p-6 md:p-8 h-full flex flex-col">
                  <Quote size={32} className="text-[#D4A843]/40 mb-4 flex-shrink-0" />
                  <blockquote className="font-playfair text-[#1B4332] italic leading-relaxed mb-6 flex-1">
                    "{t.quote}"
                  </blockquote>
                  <div className="flex items-center gap-3 mt-auto">
                    <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                    <div>
                      <p className="font-outfit font-semibold text-[#1B4332] text-sm">{t.name}</p>
                      <p className="font-outfit text-[#666] text-xs">{t.location}</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
